<template>
    <div>
        <h2 is="sui-header">{{ $t('gateway.fileConfig.title') }}</h2>

        <sui-form-fields inline>
            <label>{{ $t('common.file') }}</label>
            <sui-form-field>
                <sui-checkbox
                    radio
                    :label="$t('gateway.fileConfig.shared')"
                    value="SHARED"
                    v-model="activeGateway.configuration.fileConfiguration.ownership"
                    required
                />
            </sui-form-field>
            <sui-form-field>
                <sui-checkbox
                    radio
                    :label="$t('gateway.fileConfig.individual')"
                    value="INDIVIDUAL"
                    v-model="activeGateway.configuration.fileConfiguration.ownership"
                    required
                />
            </sui-form-field>
        </sui-form-fields>

        <sui-form-field v-if="fetchMethod !== 'URL'">
            <sui-checkbox
                :label="$t('gateway.fileConfig.isZipFile')"
                v-model="activeGateway.configuration.fileConfiguration.isZip"
            />
        </sui-form-field>

        <sui-form-fields :fields="fetchMethod === 'URL' ? 'three' : 'four'">
            <sui-form-field v-if="fetchMethod !== 'URL'">
                <label>{{ $t('gateway.fileConfig.zipFileName') }}</label>
                <div class="ui right labeled input">
                    <div class="ui label"><sui-icon name="bullseye violet small icon"></sui-icon></div>
                    <input
                        :disabled="activeGateway.configuration.fileConfiguration.isZip === false"
                        @focus="onInputFocus"
                        v-model="zipName"
                        type="text"
                        placeholder="adaptimmo.zip, 4785.zip, ..."
                        required
                    />
                    <div class="ui label">.zip</div>
                </div>
                <MetadataInputList :value="activeGateway.configuration.fileConfiguration.zipName" property="zipName"/>
            </sui-form-field>

            <sui-form-field>
                <label>{{ $t('gateway.fileConfig.adsFileName') }}</label>
                <div class="ui right labeled input">
                    <div class="ui label"><sui-icon name="bullseye violet small icon"></sui-icon></div>
                    <input
                        :disabled="fetchMethod === 'URL'"
                        @focus="onInputFocus"
                        @input="onFileNameChange"
                        v-model="fileName"
                        type="text"
                        placeholder="annonces.xml, confiance-immo.json, ..."
                        required
                    />
                    <sui-dropdown button pointing class="label" :text="this.activeGateway.configuration.fileConfiguration.fileExtension">
                        <sui-dropdown-menu>
                            <sui-dropdown-item @click="changeSelectedExtension('.csv')">.csv</sui-dropdown-item>
                            <sui-dropdown-item @click="changeSelectedExtension('.xml')">.xml</sui-dropdown-item>
                            <sui-dropdown-item @click="changeSelectedExtension('.json')">.json</sui-dropdown-item>
                            <sui-dropdown-item @click="changeSelectedExtension('.txt')">.txt</sui-dropdown-item>
                        </sui-dropdown-menu>
                    </sui-dropdown>
                </div>
                <MetadataInputList :value="activeGateway.configuration.fileConfiguration.fileName" property="fileName"/>
            </sui-form-field>

            <sui-form-field>
                <label>{{ $t('gateway.fileConfig.fileType') }}</label>

                <sui-dropdown button fluid pointing :text="this.activeGateway.configuration.fileConfiguration.fileType">
                    <sui-dropdown-menu>
                        <sui-dropdown-item @click="changeSelectedFileType('CSV')">CSV</sui-dropdown-item>
                        <sui-dropdown-item @click="changeSelectedFileType('XML')">XML</sui-dropdown-item>
                        <sui-dropdown-item @click="changeSelectedFileType('JSON')">JSON</sui-dropdown-item>
                    </sui-dropdown-menu>
                </sui-dropdown>

                <sui-list bulleted class="mini" v-if="fetchMethod !== 'URL'">
                    <sui-list-item class="no-bullet">
                        {{ $t('gateway.fileConfig.fileTypeWarning') }}
                        <br>
                        {{ $t('common.example') }}: <code>Annonces.txt</code> {{ $t('gateway.fileConfig.fileTypeWarningExample') }} <code>CSV</code>
                    </sui-list-item>
                </sui-list>
            </sui-form-field>

            <sui-form-field>
                <label>{{ $t('gateway.fileConfig.fileSeparator') }}</label>
                <input
                    type="text"
                    placeholder=""
                    :disabled="activeGateway.configuration.fileConfiguration.fileType !== 'CSV'"
                    v-model="activeGateway.configuration.fileConfiguration.fileSeparator"
                />

                <sui-list bulleted class="mini preformated-input-list no-bullet" v-if="fetchMethod !== 'URL'">
                    <sui-list-item>
                        {{ $t('gateway.fileConfig.fileSeparatorInfo') }}
                    </sui-list-item>
                </sui-list>
            </sui-form-field>
        </sui-form-fields>

        <sui-divider section />
    </div>
</template>

<script>
    import MetadataInputList from '@/components/gateway/MetadataInputList';
    import store from '@/store';
    import { mapGetters } from 'vuex';

    export default {
        name: 'FileConfig',
        props: {
            fetchMethod: String,
        },
        components: {
            MetadataInputList,
        },
        methods: {
            onInputFocus(e) {
                this.state.forms.gateway.currentSelectedInput = e.target;
            },
            changeSelectedExtension(extension) {
                const { fileExtension: previousExtension, fileName } = this.activeGateway.configuration.fileConfiguration;

                this.activeGateway.configuration.fileConfiguration.fileExtension = extension;
                this.activeGateway.configuration.fileConfiguration.fileName = fileName.replace(previousExtension, extension);

                if (extension !== '.txt') {
                    this.changeSelectedFileType(extension.toUpperCase().replace('.', ''));
                }
            },
            changeSelectedFileType(type) {
                this.activeGateway.configuration.fileConfiguration.fileType = type;
            },
            onFileNameChange(event) {
                this.setFileName(event.target.value);
            },
            setFileName(value) {
                const extensions = ['.csv', '.json', '.xml', '.txt'];

                this.activeGateway.configuration.fileConfiguration.fileName = value;

                for (const ext of extensions) {
                    if (value.endsWith(ext)) {
                        this.changeSelectedExtension(ext);
                    }
                }

                const { fileName, fileExtension } = this.activeGateway.configuration.fileConfiguration;

                const extIdx = fileName.split('.').findIndex(fragment => {
                    return extensions.includes(`.${fragment}`);
                });

                // Si pas d'extension trouvée, on rajoute l'extension par défaut ou celle sélectionnée.
                if (extIdx === -1) {
                    this.activeGateway.configuration.fileConfiguration.fileName = fileName + fileExtension;
                }
            },
        },
        beforeMount() {
            if (this.fetchMethod === 'URL') {
                this.activeGateway.configuration.fileConfiguration.isZip = false;
            }
        },
        computed: {
            ...mapGetters(['activeGateway']),
            fileName: {
                get() {
                    if (this.fetchMethod === 'URL') {
                        this.setFileName('listings_file');
                    }

                    const { fileName, fileExtension } = this.activeGateway.configuration.fileConfiguration;

                    if (!fileName) {
                        return '';
                    }

                    return fileName.replace(fileExtension, '');
                },
                set(value) {
                    this.setFileName(value);
                }
            },
            zipName: {
                get() {
                    const { zipName } = this.activeGateway.configuration.fileConfiguration;

                    return (zipName && zipName.endsWith('.zip'))
                        ? zipName.split('.zip')[0]
                        : zipName;
                },
                set(value) {
                    this.activeGateway.configuration.fileConfiguration.zipName = value;
                }
            },
            state: {
                get() { return store.state; }
            },
        },
    }
</script>

<style>
    .label .icon {
        margin: 0 !important;
    }

    code {
        background-color: #E0E1E2;
        padding: 3px 6px;
        border-radius: 3px;
        color: #6435C9;
    }
</style>